import { getContactPersonsByDistrictCode } from '@hub/libs/catalog-service';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { Dropdown } from '@hub/common/Dropdown/Dropdown';
import { Link } from '@hub/common/Link/Link';
import { IconID } from '@hub/common/Icon/Icon';
import { getLocale } from '@hub/libs/constants';
import { IGUS_REWRITE, EXTERNAL_LINKS } from '@hub/libs/redirect.helper';
import classNames from 'classnames';
import styles from '../ContactPersonBox/ContactPersonBox.module.scss';
import { openPopupWin } from '@hub/libs/ui.helper';
import { useRouter } from 'next/router';
import type { ContactPerson } from '@hub/models/graphql';
import { useTranslation } from 'next-i18next';
import { getLanguageAlternates } from '@hub/libs/i18n.helper';

export interface ContactPersonBoxDistrictAreaTypeProps {
  districts: (string | number)[][];
}

export const ContactPersonBoxDistrictAreaType = ({ districts }: ContactPersonBoxDistrictAreaTypeProps) => {
  const [districtCode, setDistrictCode] = useState('');
  const [contactPersonsResults, setContactPersonsResults] = useState<ContactPerson[]>([]);
  const { t } = useTranslation('layout/contactPersonBox');
  const router = useRouter();
  const locale = getLocale(router);
  const defaultUrl = getLanguageAlternates().find(alternate => alternate.hrefLang === locale)?.href;

  const availableDistricts = new Map();
  availableDistricts.set(null, '');

  for (const district of districts) {
    availableDistricts.set(district[1], district[0]);
  }

  useEffect(() => {
    const localStorageDistrictCode = localStorage.getItem('districtCode');
    if (localStorageDistrictCode && !districtCode) {
      setDistrictCode(localStorageDistrictCode);
    }
    const localStorageContacts: string = localStorage.getItem('districtCodeContacts') ?? '';
    if (localStorageContacts) {
      const contacts = JSON.parse(localStorageContacts) as unknown as ContactPerson[];
      if (contacts && contacts.length > 0 && contactPersonsResults.length === 0) {
        setContactPersonsResults(contacts);
      }
    }
  }, []);

  const handleGetContactPersonsDistrictCode = async (code: string) => {
    try {
      const contactPersons = await getContactPersonsByDistrictCode(code, defaultUrl);
      setContactPersonsResults(contactPersons);
      localStorage.setItem('districtCodeContacts', JSON.stringify(contactPersons));
    } catch (error) {
      Sentry.captureException(new Error(`Could not getContactPersonsByZipCode - ${error as string}`));
    }
  };

  const openWriteContactPerson = (link: string) => () => openPopupWin(link, 'CONTACT', true, false, 400, 525);

  const onDistrictSelectionChange = async (value: string) => {
    await handleGetContactPersonsDistrictCode(value);
    for (const availableDistrict of availableDistricts.entries()) {
      if (availableDistrict[1] === Number(value)) {
        setDistrictCode(availableDistricts.get(availableDistrict[0]) as string);
        localStorage.setItem('districtCode', availableDistricts.get(availableDistrict[0]) as string);
      }
    }
  };

  const frenchLinks =
    locale === 'fr-FR' ? (
      <div key="french-links">
        <Link
          text={t('CONTACT_PERSON_BOX.IN_FRANCE_LINK')}
          icon={IconID.ARROW_RIGHT}
          bigIcon={false}
          href={EXTERNAL_LINKS.SALES.SALES_FR()}
          className="text-xs"
          primary
        />
        <Link
          text={t('CONTACT_PERSON_BOX.ABROAD_LINK')}
          icon={IconID.ARROW_RIGHT}
          bigIcon={false}
          href={EXTERNAL_LINKS.CONTACT_PERSON.SEE_ALL_CONTACT_FR()}
          className="text-xs"
          primary
        />
      </div>
    ) : null;

  return (
    <div
      data-testid="district-area-type"
      className={classNames(styles.box, 'border', 'border-gray-200', 'absolute', 'px-12', 'bg-white', 'z-50', 'pb-6')}
    >
      {contactPersonsResults?.length > 0 ? (
        contactPersonsResults.map(contact => (
          <div
            className="flex gap-4 flex-col px-12 py-12 border-b border-gray-100"
            key={`${contact.name}_${contact.messageParams?.join('_') ?? ''}`}
          >
            <h6 className="mb-4 text-xl">{contact.businessUnit}</h6>
            <div className="flex flex-row gap-2 items-center">
              <div>
                <p className="text-s">{contact.name}</p>
                {contact.phone && (
                  <div className="mb-2 text-gray-500 text-xs text-align-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      className="inline-block"
                      style={{ width: '1em', height: '1em', verticalAlign: '-0.125em' }}
                    >
                      <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
                    </svg>
                    <span className="ml-2">{contact.phone}</span>
                  </div>
                )}
              </div>
              {contact.photo && (
                <img
                  data-testid="district-img"
                  loading="lazy"
                  alt={`${contact.businessUnit || ''} ${contact.name}`}
                  className="rounded-full h-20 w-20 float-right"
                  src={IGUS_REWRITE(contact.photo, locale)}
                />
              )}
            </div>
            {contact.messageParams?.length === 3 && (
              <Link
                data-testid="link-message"
                text={t('CONTACT_PERSON_BOX.WRITE_MESSAGE')}
                onClick={openWriteContactPerson(
                  EXTERNAL_LINKS.CONTACT_PERSON.WRITE_MESSAGE(
                    contact.messageParams[0],
                    contact.messageParams[1],
                    contact.messageParams[2],
                    locale
                  )
                )}
                icon={IconID.ARROW_RIGHT}
                external
                bigIcon={false}
                iconPrimary
                className="text-xxs"
                primary={false}
              />
            )}
          </div>
        ))
      ) : (
        <div className="mt-12">
          {frenchLinks}
          <h6 className="my-4 text-sm text-gray-500">{t('CONTACT_PERSON_BOX.SELECT_LOCATION.TITLE')}</h6>
          <Dropdown
            className="max-w-full md:max-w-md mb-6"
            values={availableDistricts}
            selected={districtCode}
            id="sort"
            onChange={value => void onDistrictSelectionChange(value)}
          />
        </div>
      )}
    </div>
  );
};
